"use client";

import { useState } from "react";
import { Checkbox, CopyButton, Notice } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { MeshTermFinderStrings } from "@/components/tools/mesh-term-finder";
import {
  buildPubmedString,
  freeTextCandidates,
  type MeshDescriptor,
  type PubmedOptions,
  pubmedSearchUrl,
  skipKey,
} from "@/lib/tool-kit/research-discovery/mesh";

const LINK = "break-all text-primary underline-offset-4 hover:underline";

export function MeshBuilder({
  basket,
  strings,
  onRemove,
  onClear,
}: {
  basket: readonly MeshDescriptor[];
  strings: MeshTermFinderStrings;
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  const [options, setOptions] = useState<PubmedOptions>({
    explode: true,
    majorTopic: false,
    freeText: true,
  });
  const [skip, setSkip] = useState<ReadonlySet<string>>(new Set());
  const query = buildPubmedString(basket, { ...options, skip });

  function toggle(key: keyof PubmedOptions) {
    return (checked: boolean) =>
      setOptions((current) => ({ ...current, [key]: checked }));
  }

  function toggleTerm(key: string, on: boolean) {
    setSkip((current) => {
      const next = new Set(current);
      if (on) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <div className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.builderHeading}</h3>
      {basket.length === 0 ? (
        <Notice>{strings.basketEmpty}</Notice>
      ) : (
        <>
          <ul className="flex flex-wrap gap-2">
            {basket.map((item) => (
              <li
                className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm"
                key={item.id}
              >
                {item.label}
                <button
                  aria-label={`${strings.remove}: ${item.label}`}
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => onRemove(item.id)}
                  type="button"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <fieldset className="space-y-2">
            <legend className="font-medium text-foreground text-sm">
              {strings.optionsLegend}
            </legend>
            <Checkbox
              checked={options.explode}
              label={strings.optExplode}
              onChange={toggle("explode")}
            />
            <Checkbox
              checked={options.majorTopic}
              label={strings.optMajor}
              onChange={toggle("majorTopic")}
            />
            <Checkbox
              checked={options.freeText}
              label={strings.optFreeText}
              onChange={toggle("freeText")}
            />
          </fieldset>
          {options.freeText ? (
            <details className="space-y-2">
              <summary className="cursor-pointer font-medium text-foreground text-sm">
                {strings.chooseWords}
              </summary>
              <div className="mt-2 space-y-3">
                {basket.map((item) => (
                  <fieldset className="space-y-1" key={item.id}>
                    <legend className="font-medium text-foreground text-sm">
                      {item.label}
                    </legend>
                    {freeTextCandidates(item).map((term) => (
                      <Checkbox
                        checked={!skip.has(skipKey(item.id, term))}
                        key={term}
                        label={term}
                        onChange={(on) =>
                          toggleTerm(skipKey(item.id, term), on)
                        }
                      />
                    ))}
                  </fieldset>
                ))}
              </div>
            </details>
          ) : null}
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
            {query}
          </pre>
          <div className="flex flex-wrap items-center gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyString}
              text={query}
            />
            <a
              className={LINK}
              href={pubmedSearchUrl(query)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {strings.openPubmed}
            </a>
            <button
              className={BUTTON_SECONDARY}
              onClick={onClear}
              type="button"
            >
              {strings.clearBasket}
            </button>
          </div>
          <p className={HINT}>{strings.builderNote}</p>
        </>
      )}
    </div>
  );
}
